import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import type { RootState } from '../../../store';
import SecureImage from '../../../components/Media/SecureImage';
import {
  getContinueWatching,
  removeFromContinueWatching,
  type ContinueWatchingItem,
} from '../../../services/watchProgressService';
import { routeFor, timeLeft } from './continueWatching';

/**
 * The signed-in viewer's Continue watching row.
 *
 * Renders nothing at all when there is nothing to continue - an empty row with a heading
 * is clutter, and on a first visit it would be the most prominent thing on the page
 * saying "you have not watched anything".
 */

const ContinueWatchingRow: React.FC = () => {
  const isAuthenticated = useSelector((state: RootState) => !!state.user.userInfo);
  const navigate = useNavigate();
  const [items, setItems] = useState<ContinueWatchingItem[]>([]);

  useEffect(() => {
    if (!isAuthenticated) {
      setItems([]);
      return;
    }
    let cancelled = false;
    getContinueWatching(20)
      .then((row) => { if (!cancelled) setItems(row); })
      // A failed fetch hides the row rather than showing an error on the home page.
      .catch(() => { if (!cancelled) setItems([]); });
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  if (!isAuthenticated || items.length === 0) return null;

  const remove = (contentId: string) => {
    // Optimistic: removing from a row should feel instant. If the request fails the
    // title simply reappears on the next visit, which is a harmless way to be wrong.
    setItems((prev) => prev.filter((i) => i.content.id !== contentId));
    removeFromContinueWatching(contentId).catch(() => {});
  };

  return (
    // An explicit background rather than inheriting the page's. The section above is dark
    // and the one below is white, so text colours chosen against "the page" are a guess -
    // and the first guess was wrong: near-white text, copied from a component that sits
    // inside dark cards, rendered white on white.
    <section aria-labelledby="continue-watching-heading" className="bg-white py-6 sm:py-8 px-4">
      <div className="max-w-7xl mx-auto">
        <h2 id="continue-watching-heading" className="text-xl sm:text-2xl font-bold mb-4 text-gray-900">
          Continue watching
        </h2>
        <ul className="flex gap-4 overflow-x-auto pb-2 snap-x">
          {items.map(({ content, progress }) => (
            <li key={content.id} className="relative flex-none w-64 snap-start group">
              <button
                type="button"
                onClick={() => navigate(routeFor(content.contentType, content.id, content.slug))}
                className="block w-full text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 rounded-lg"
                aria-label={`Continue watching ${content.title}, ${timeLeft(progress.positionSeconds, progress.durationSeconds)}`}
              >
                <div className="relative aspect-video overflow-hidden rounded-lg bg-slate-800">
                  {content.thumbnailUrl && (
                    <SecureImage
                      src={content.thumbnailUrl}
                      alt=""
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                      loading="lazy"
                    />
                  )}
                  <div className="absolute inset-x-0 bottom-0 h-1 bg-white/20">
                    <div
                      className="h-full bg-orange-500"
                      style={{ width: `${Math.round(progress.progress * 100)}%` }}
                    />
                  </div>
                </div>
                <p className="mt-2 text-sm font-semibold text-gray-900 truncate">{content.title}</p>
                <p className="text-xs text-gray-600">
                  {timeLeft(progress.positionSeconds, progress.durationSeconds)}
                </p>
              </button>
              {/* Always visible on small screens: touch has no hover, so a hover-only
                  button would be invisible on exactly the devices most people use. */}
              <button
                type="button"
                onClick={() => remove(content.id)}
                aria-label={`Remove ${content.title} from Continue watching`}
                className="absolute top-2 right-2 rounded-full bg-black/70 px-2 text-white opacity-100 sm:opacity-0 sm:group-hover:opacity-100 focus-visible:opacity-100 transition-opacity"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

export default ContinueWatchingRow;
